/**
 * Lightweight in-game logger with on-screen overlay for mobile debugging.
 * Captures errors, warnings, and info messages. Stores a rolling buffer
 * so logs can be inspected on devices without dev tools.
 *
 * The overlay is hidden by default and toggled via a small button.
 */

export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
}

const MAX_ENTRIES = 200;
const OVERLAY_LINES = 8;

class Logger {
  private entries: LogEntry[] = [];
  private overlay: HTMLDivElement | null = null;
  private toggleBtn: HTMLButtonElement | null = null;
  private overlayVisible = false;

  /**
   * Create the overlay DOM and toggle button.
   * The overlay starts hidden; use the button to show/hide.
   */
  initOverlay(): void {
    if (this.overlay) return;

    // Toggle button — small fixed button in the top-right corner
    const btn = document.createElement('button');
    btn.id = 'debug-toggle';
    btn.textContent = 'LOG';
    btn.style.cssText = [
      'position:fixed',
      'top:4px',
      'right:4px',
      'z-index:100000',
      'background:rgba(40,40,40,0.7)',
      'color:#0f0',
      'border:1px solid #0f0',
      'border-radius:4px',
      'font:bold 10px/1 monospace',
      'padding:4px 8px',
      'cursor:pointer',
      'touch-action:manipulation',
      '-webkit-tap-highlight-color:transparent',
    ].join(';');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleOverlay();
    });
    document.body.appendChild(btn);
    this.toggleBtn = btn;

    // Overlay panel
    const div = document.createElement('div');
    div.id = 'debug-overlay';
    div.style.cssText = [
      'position:fixed',
      'top:28px',
      'left:0',
      'right:0',
      'z-index:99999',
      'background:rgba(0,0,0,0.85)',
      'color:#0f0',
      'font:11px/1.4 monospace',
      'padding:6px 8px',
      'pointer-events:none',
      'white-space:pre-wrap',
      'max-height:30vh',
      'overflow:hidden',
      'display:none',
    ].join(';');
    document.body.appendChild(div);
    this.overlay = div;
  }

  /**
   * Toggle overlay visibility.
   */
  private toggleOverlay(): void {
    if (!this.overlay || !this.toggleBtn) return;
    this.overlayVisible = !this.overlayVisible;
    this.overlay.style.display = this.overlayVisible ? 'block' : 'none';
    this.toggleBtn.style.color = this.overlayVisible ? '#fa0' : '#0f0';
    this.toggleBtn.style.borderColor = this.overlayVisible ? '#fa0' : '#0f0';
    if (this.overlayVisible) {
      this.refreshOverlay();
    }
  }

  private push(level: LogLevel, message: string): void {
    const entry: LogEntry = { level, message, timestamp: Date.now() };
    this.entries.push(entry);
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.shift();
    }

    // Mirror to real console
    const consoleFn =
      level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    consoleFn(`[DK:${level}] ${message}`);

    if (this.overlayVisible) {
      this.refreshOverlay();
    }
  }

  error(msg: string, err?: unknown): void {
    const detail =
      err instanceof Error ? `: ${err.message}` : err !== undefined ? `: ${String(err)}` : '';
    this.push('error', msg + detail);
  }

  warn(msg: string): void {
    this.push('warn', msg);
  }

  info(msg: string): void {
    this.push('info', msg);
  }

  debug(msg: string): void {
    this.push('debug', msg);
  }

  /** Return recent entries (newest first). */
  getEntries(count = 50): readonly LogEntry[] {
    return this.entries.slice(-count).reverse();
  }

  private refreshOverlay(): void {
    if (!this.overlay) return;
    const recent = this.entries.slice(-OVERLAY_LINES);

    if (recent.length === 0) {
      this.overlay.innerHTML = '<span style="color:#888">(no log entries)</span>';
      return;
    }

    const colorMap: Record<LogLevel, string> = {
      error: '#f44',
      warn: '#fa0',
      info: '#0f0',
      debug: '#888',
    };
    this.overlay.innerHTML = recent
      .map((e) => {
        const c = colorMap[e.level];
        const ts = new Date(e.timestamp).toLocaleTimeString();
        return `<span style="color:${c}">[${ts} ${e.level.toUpperCase()}] ${this.escapeHtml(e.message)}</span>`;
      })
      .join('\n');
  }

  private escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

/** Singleton logger instance. */
export const logger = new Logger();
